import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getReports } from '../services/api';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { showError } from '../services/swal';
import { Line, Doughnut } from 'react-chartjs-2';
import { 
    Chart as ChartJS, 
    CategoryScale, 
    LinearScale, 
    PointElement, 
    LineElement, 
    Title, 
    Tooltip, 
    Legend, 
    ArcElement, 
    Filler 
} from 'chart.js';

ChartJS.register(
    CategoryScale, 
    LinearScale, 
    PointElement, 
    LineElement, 
    Title, 
    Tooltip, 
    Legend, 
    ArcElement, 
    Filler
);

const Reports = () => {
    const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)));
    const [endDate, setEndDate] = useState(new Date());
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('sales_summary');

    const fetchReports = async () => {
        setLoading(true);
        try {
            const params = {
                start_date: startDate.toISOString().split('T')[0],
                end_date: endDate.toISOString().split('T')[0],
            };
            const response = await getReports(params);
            setReportData(response.data);
        } catch (error) {
            console.error("Failed to fetch reports", error);
            showError('Report Failed', 'Failed to fetch reports. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReports();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const lineChartData = {
        labels: reportData?.sales_over_time.map(d => d.date) || [],
        datasets: [
            {
                label: 'Revenue (Rs.)',
                data: reportData?.sales_over_time.map(d => d.total) || [],
                borderColor: '#6C5DD3',
                backgroundColor: 'rgba(108, 93, 211, 0.1)',
                fill: true,
                tension: 0.4,
                pointRadius: 4,
                pointBackgroundColor: '#6C5DD3',
            },
        ],
    };

    const doughnutData = {
        labels: reportData?.top_categories.map(c => c.name) || [],
        datasets: [
            {
                data: reportData?.top_categories.map(c => c.total_revenue) || [],
                backgroundColor: ['#6C5DD3', '#FFA500', '#00C4DF', '#FF4B5C', '#27AE60'],
                borderWidth: 0,
            },
        ],
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div className="d-flex flex-column justify-content-center align-items-center py-5">
                    <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }}></div>
                    <p className="text-muted fw-bold">Compiling Analytical Data...</p>
                </div>
            );
        }

        if (!reportData) return null;

        switch (activeTab) {
            case 'sales_summary':
                return (
                    <div className="animate__animated animate__fadeIn">
                        <div className="row g-4 mb-4">
                            <div className="col-12 col-md-6 col-xl-3">
                                <div className="report-stat-card border-0 shadow-sm">
                                    <div className="icon-badge bg-primary bg-opacity-10 text-primary mb-3">
                                        <i className="bi bi-cash-stack fs-4"></i>
                                    </div>
                                    <small className="text-muted d-block fw-bold mb-1">Net Revenue</small>
                                    <h3 className="fw-bold mb-0">Rs. {parseFloat(reportData.summary.total_revenue || 0).toLocaleString()}</h3>
                                </div>
                            </div>
                            <div className="col-12 col-md-6 col-xl-3">
                                <div className="report-stat-card border-0 shadow-sm">
                                    <div className="icon-badge bg-success bg-opacity-10 text-success mb-3">
                                        <i className="bi bi-graph-up-arrow fs-4"></i>
                                    </div>
                                    <small className="text-muted d-block fw-bold mb-1">Estimated Profit</small>
                                    <h3 className="fw-bold mb-0 text-success">Rs. {parseFloat(reportData.summary.total_profit || 0).toLocaleString()}</h3>
                                </div>
                            </div>
                            <div className="col-12 col-md-6 col-xl-3">
                                <div className="report-stat-card border-0 shadow-sm">
                                    <div className="icon-badge bg-info bg-opacity-10 text-info mb-3">
                                        <i className="bi bi-cart-check fs-4"></i>
                                    </div>
                                    <small className="text-muted d-block fw-bold mb-1">Total Sales</small>
                                    <h3 className="fw-bold mb-0">{reportData.summary.total_sales || 0} Invoices</h3>
                                </div>
                            </div>
                            <div className="col-12 col-md-6 col-xl-3">
                                <div className="report-stat-card border-0 shadow-sm">
                                    <div className="icon-badge bg-warning bg-opacity-10 text-warning mb-3">
                                        <i className="bi bi-box-seam fs-4"></i>
                                    </div>
                                    <small className="text-muted d-block fw-bold mb-1">Items Sold</small>
                                    <h3 className="fw-bold mb-0">{reportData.summary.total_items_sold || 0} Units</h3>
                                </div>
                            </div>
                        </div>

                        <div className="row g-4">
                            <div className="col-12 col-xl-8">
                                <div className="chart-container shadow-sm border-0">
                                    <div className="d-flex justify-content-between align-items-center mb-4">
                                        <h5 className="fw-bold mb-0">Revenue Growth Trend</h5>
                                        <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2">Daily Performance</span>
                                    </div>
                                    <div style={{ height: '350px' }}>
                                        <Line 
                                            data={lineChartData} 
                                            options={{ 
                                                responsive: true, 
                                                maintainAspectRatio: false,
                                                plugins: { legend: { display: false } },
                                                scales: {
                                                    y: { grid: { display: false } },
                                                    x: { grid: { display: false } }
                                                }
                                            }} 
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-xl-4">
                                <div className="chart-container shadow-sm border-0 h-100">
                                    <h5 className="fw-bold mb-4">Category Distribution</h5>
                                    <div style={{ height: '280px' }} className="d-flex justify-content-center">
                                        <Doughnut 
                                            data={doughnutData} 
                                            options={{ 
                                                responsive: true, 
                                                maintainAspectRatio: false,
                                                plugins: { legend: { position: 'bottom' } }
                                            }} 
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'top_products':
                return (
                    <div className="report-table shadow-sm border-0 animate__animated animate__fadeIn">
                        <div className="table-responsive">
                            <table className="table align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th className="ps-4">Product Name</th>
                                        <th className="text-center">Units Sold</th>
                                        <th className="text-end">Revenue Generated</th>
                                        <th className="text-end pe-4">Stock Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportData.top_products.map(p => (
                                        <tr key={p.name}>
                                            <td className="ps-4 py-3 fw-bold">{p.name}</td>
                                            <td className="text-center"><span className="badge bg-light text-dark px-3">{p.total_quantity}</span></td>
                                            <td className="text-end fw-bold text-primary">Rs. {parseFloat(p.total_revenue).toLocaleString()}</td>
                                            <td className="text-end pe-4 text-muted">Rs. {parseFloat(p.stock_value || 0).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            case 'inventory_report':
                return (
                    <div className="report-table shadow-sm border-0 animate__animated animate__fadeIn">
                        <div className="table-responsive">
                            <table className="table align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th className="ps-4">Item Identity</th>
                                        <th>Category</th>
                                        <th className="text-center">Available Stock</th>
                                        <th className="text-end pe-4">Estimated Value (Cost)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportData.inventory_report.map(i => (
                                        <tr key={i.name}>
                                            <td className="ps-4 py-3">
                                                <div className="fw-bold">{i.name}</div>
                                                <small className="text-muted">{i.sku || 'N/A'}</small>
                                            </td>
                                            <td><span className="badge bg-primary bg-opacity-10 text-primary border-0">{i.category}</span></td>
                                            <td className="text-center">
                                                <span className={`fw-bold ${i.stock < 10 ? 'text-danger' : 'text-dark'}`}>{i.stock}</span>
                                            </td>
                                            <td className="text-end pe-4 fw-bold text-dark">Rs. {parseFloat(i.stock_value).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <Layout title="Business Intelligence">
            {/* Discovery Bar */}
            <div className="report-discovery-bar shadow-sm border-0">
                <div className="row g-4 align-items-end">
                    <div className="col-12 col-md-4 col-lg-3">
                        <label className="form-label small fw-bold text-muted mb-2">Start Date</label>
                        <div className="input-group">
                            <DatePicker 
                                selected={startDate} 
                                onChange={date => setStartDate(date)} 
                                className="form-control border-0 bg-light" 
                                placeholderText="Select start"
                            />
                        </div>
                    </div>
                    <div className="col-12 col-md-4 col-lg-3">
                        <label className="form-label small fw-bold text-muted mb-2">End Date</label>
                        <div className="input-group">
                            <DatePicker 
                                selected={endDate} 
                                onChange={date => setEndDate(date)} 
                                className="form-control border-0 bg-light" 
                                placeholderText="Select end"
                            />
                        </div>
                    </div>
                    <div className="col-12 col-md-4 col-lg-6 d-flex gap-3">
                        <button className="btn btn-primary px-4 fw-bold flex-grow-1" onClick={fetchReports} disabled={loading} style={{ height: '48px', borderRadius: '12px' }}>
                            {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Processing...</> : <><i className="bi bi-lightning-charge-fill me-2"></i>Analyze Data</>}
                        </button>
                        <button className="btn btn-light border px-4 fw-bold" onClick={() => window.print()} style={{ height: '48px', borderRadius: '12px' }} title="Download PDF">
                            <i className="bi bi-file-earmark-pdf"></i>
                        </button>
                    </div>
                </div>
            </div>

            {/* Navigation Pills */}
            <div className="row g-3 mb-4">
                {[
                    { id: 'sales_summary', icon: 'bi-grid-1x2-fill', label: 'Dashboard' },
                    { id: 'top_products', icon: 'bi-trophy-fill', label: 'Top Performance' },
                    { id: 'inventory_report', icon: 'bi-box-fill', label: 'Stock Audit' }
                ].map(tab => (
                    <div key={tab.id} className="col-6 col-md-4 col-lg-auto">
                        <button 
                            className={`nav-link w-100 rounded-pill px-4 py-3 fw-bold d-flex align-items-center gap-3 transition-all analytical-tab ${activeTab === tab.id ? 'active shadow-sm' : 'bg-white border'}`} 
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <div className={`icon-badge shadow-sm ${activeTab === tab.id ? 'bg-white text-primary' : 'bg-primary bg-opacity-10 text-primary'}`} style={{ width: '32px', height: '32px', fontSize: '0.9rem' }}>
                                <i className={`bi ${tab.icon}`}></i>
                            </div>
                            <span className="small">{tab.label}</span>
                        </button>
                    </div>
                ))}
            </div>

            {/* Analytical Workspace */}
            <div className="analytical-workspace">
                {renderContent()}
            </div>
            <style>{`
                .report-stat-card {
                    background: var(--card-bg);
                    border-radius: 20px;
                    padding: 24px;
                    border: 1px solid var(--border-color);
                    transition: all 0.3s ease;
                }
                .report-stat-card:hover {
                    transform: translateY(-5px);
                    box-shadow: var(--card-shadow);
                }
                .chart-container {
                    background: var(--card-bg);
                    border-radius: 24px;
                    padding: 24px;
                    border: 1px solid var(--border-color);
                }
                .report-discovery-bar {
                    background: var(--card-bg);
                    border-radius: 24px;
                    padding: 24px;
                    border: 1px solid var(--border-color);
                    margin-bottom: 2rem;
                }
                .analytical-tab {
                    border: 1px solid var(--border-color);
                    transition: all 0.3s ease;
                }
                .analytical-tab.active {
                    background: var(--primary-color) !important;
                    color: white !important;
                    border-color: var(--primary-color) !important;
                }
                .report-table {
                    background: var(--card-bg);
                    border-radius: 24px;
                    overflow: hidden;
                    border: 1px solid var(--border-color);
                }
                .icon-badge {
                    width: 45px;
                    height: 45px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.25rem;
                }
                
                /* Dark Mode Overrides */
                [data-bs-theme="dark"] .bg-white,
                [data-bs-theme="dark"] .bg-light {
                    background-color: var(--card-bg) !important;
                    color: var(--text-color) !important;
                }
                [data-bs-theme="dark"] .analytical-tab:not(.active) {
                    background-color: rgba(255,255,255,0.03) !important;
                    color: var(--text-color) !important;
                }
                [data-bs-theme="dark"] .react-datepicker__input-container input {
                    background-color: rgba(255,255,255,0.03) !important;
                    border: 1px solid var(--border-color) !important;
                    color: var(--text-color) !important;
                }
                [data-bs-theme="dark"] .table {
                    --bs-table-bg: transparent;
                    --bs-table-color: var(--text-color);
                }
                [data-bs-theme="dark"] .text-dark {
                    color: var(--text-color) !important;
                }
                [data-bs-theme="dark"] .text-muted {
                    color: var(--text-muted-color) !important;
                }
            `}</style>
        </Layout>
    );
};

export default Reports;
